import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Brand } from "@/components/site/brand";
import { DisclaimerBar } from "@/components/site/disclaimer-bar";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck } from "lucide-react";

function useEmailVerificationRequired() {
  const [required, setRequired] = useState<boolean | null>(null);
  useEffect(() => {
    supabase.from("app_settings").select("value").eq("key", "email_verification_required").maybeSingle()
      .then(({ data }) => setRequired(Boolean(data?.value)));
  }, []);
  return required;
}

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Login or Register — CryptoBull Pro" },
      { name: "description", content: "Sign in to your CryptoBull Pro account or register a new one." },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"login" | "register">("login");
  const verifyRequired = useEmailVerificationRequired();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/dashboard" });
    });
  }, [navigate]);

  return (
    <div className="min-h-screen">
      <DisclaimerBar />
      <div className="min-h-[calc(100vh-26px)] px-4 py-10 sm:py-16 flex flex-col items-center justify-center">
        <Brand size="lg" />
        <div className="mt-8 w-full max-w-md sm:max-w-lg glass rounded-3xl p-7 sm:p-10">
          <div className="mb-4 flex justify-center">
            {verifyRequired === null ? null : verifyRequired ? (
              <Badge variant="outline" className="text-xs">Email verification required</Badge>
            ) : (
              <Badge variant="secondary" className="text-xs">Instant access — no email code needed</Badge>
            )}
          </div>
          <div className="mb-5 rounded-xl border border-white/10 bg-white/5 px-4 py-3 flex items-start gap-3 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
            <div>
              <div className="text-foreground font-medium">Admin access</div>
              After signing in, your admin status is shown on the <span className="text-foreground">Settings</span> page. The first registered user can claim the admin role from there.
            </div>
          </div>
          <Tabs value={tab} onValueChange={(v) => setTab(v as "login" | "register")}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login">Sign in</TabsTrigger>
              <TabsTrigger value="register">Create account</TabsTrigger>
            </TabsList>
            <TabsContent value="login" className="mt-6"><LoginForm /></TabsContent>
            <TabsContent value="register" className="mt-6"><RegisterForm verifyRequired={!!verifyRequired} /></TabsContent>
          </Tabs>
          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-white/10" /> OR <div className="h-px flex-1 bg-white/10" />
          </div>
          <Button variant="outline" className="w-full" onClick={async () => {
            const r = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin + "/dashboard" });
            if (r.error) toast.error("Google sign-in failed");
          }}>
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>
          <p className="mt-5 text-center text-xs text-muted-foreground">
            <Link to="/forgot-password" className="hover:text-foreground">Forgot password?</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function LoginForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  return (
    <form className="space-y-4" onSubmit={async (e) => {
      e.preventDefault();
      const fd = new FormData(e.currentTarget);
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({
        email: String(fd.get("email")),
        password: String(fd.get("password")),
      });
      setLoading(false);
      if (error) { toast.error(error.message); return; }
      toast.success("Welcome back");
      navigate({ to: "/dashboard" });
    }}>
      <div className="space-y-2"><Label>Email</Label><Input type="email" name="email" required autoComplete="email" /></div>
      <div className="space-y-2"><Label>Password</Label><Input type="password" name="password" required autoComplete="current-password" /></div>
      <Button type="submit" className="w-full" disabled={loading}>{loading ? "Signing in…" : "Sign in"}</Button>
    </form>
  );
}

function RegisterForm({ verifyRequired }: { verifyRequired: boolean }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  return (
    <form className="space-y-4" onSubmit={async (e) => {
      e.preventDefault();
      const fd = new FormData(e.currentTarget);
      const email = String(fd.get("email"));
      const password = String(fd.get("password"));
      setLoading(true);
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin + "/dashboard",
          data: {
            full_name: String(fd.get("full_name") || ""),
            referral_code: String(fd.get("ref") || ""),
          },
        },
      });
      setLoading(false);
      if (error) { toast.error(error.message); return; }
      if (verifyRequired) {
        // Admin requires verification — sign out any auto-created session and send to verify page.
        await supabase.auth.signOut();
        try { localStorage.setItem("pending_verify_email", email); } catch {}
        toast.success("Check your email for a verification code.");
        navigate({ to: "/verify-email" });
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        await supabase.auth.signInWithPassword({ email, password });
      }
      await supabase.rpc("mark_email_verified");
      toast.success("Welcome to CryptoBull Pro! Your account is ready.");
      navigate({ to: "/dashboard" });
    }}>
      <div className="space-y-2"><Label>Full name</Label><Input name="full_name" required /></div>
      <div className="space-y-2"><Label>Email</Label><Input type="email" name="email" required autoComplete="email" /></div>
      <div className="space-y-2"><Label>Password</Label><Input type="password" name="password" minLength={8} required autoComplete="new-password" /></div>
      <div className="space-y-2"><Label>Referral code <span className="text-muted-foreground">(optional)</span></Label><Input name="ref" /></div>
      <Button type="submit" className="w-full" disabled={loading}>{loading ? "Creating…" : "Create account"}</Button>
    </form>
  );
}